import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useRef, useState, useEffect } from 'react';
import { InfoText } from './infoText.js';
import { Button } from '../ui/button.js';
import { LineIcon } from '../ui/icons/LineIcon.js';
import { useDevice } from '../../contexts/DeviceContext.js';
import { cn } from '../../lib/utils.js';

const ConsentWrapper = ({
  children,
  isError,
  helpMessage,
  className = ""
}) => {
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
    /* @__PURE__ */ jsx("div", { className: cn("round-box round-box--section", className), children }),
    helpMessage && /* @__PURE__ */ jsx(InfoText, { type: isError ? "warning" : "default", text: `${helpMessage}` })
  ] });
};
const ConsentRow = ({
  children,
  variants = "terms",
  action = "arrow",
  active = false,
  single = false,
  hasCheckbox = true,
  subCheckbox,
  onClick,
  className,
  isSuccess,
  isError,
  helpMessage,
  hasIcon = true,
  isIndented = false,
  disabled = false,
  subGrid = false,
  buttonRef,
  btnText = "확인"
}) => {
  const { isDesktop } = useDevice();
  const handleClick = () => {
    onClick?.();
  };
  const rootRef = useRef(null);
  const [title, setTitle] = useState("");
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const labelText = root.querySelector('[data-slot="field"] label .label') ?? root.querySelector(".linkTxt");
    if (!labelText) return;
    const text = labelText.textContent?.trim() ?? "";
    setTitle(text);
  }, []);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn(
          "flex flex-col first:mt-0",
          action === "confirm" && isDesktop ? "mt-5" : hasCheckbox ? "mt-5" : "mt-3 [&+*]:pt-1",
          variants === "notice" && "mt-8",
          single && "border border-basic-04 rounded-[12px] p-5",
          className
        ),
        children: [
          /* @__PURE__ */ jsxs(
            "div",
            {
              className: cn(
                "flex items-start justify-between gap-2",
                variants === "notice" && "items-center"
              ),
              ref: rootRef,
              children: [
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: cn(
                      "flex flex-col gap-1 pt-1 break-keep",
                      single && hasCheckbox && action !== "confirm" && "pt-0",
                      action === "confirm" && isDesktop && variants === "notice" && "pt-1.5"
                    ),
                    children: hasCheckbox ? /* @__PURE__ */ jsx(
                      "div",
                      {
                        className: cn(
                          "text-basic-04 whitespace-pre-wrap",
                          active && "text-primary-04"
                        ),
                        children
                      }
                    ) : /* @__PURE__ */ jsx(
                      "div",
                      {
                        className: cn("pl-8 body-lg text-basic-04 whitespace-pre-wrap linkTxt", active && "text-primary-04"),
                        children
                      }
                    )
                  }
                ),
                action === "confirm" ? /* @__PURE__ */ jsxs(
                  Button,
                  {
                    ref: buttonRef,
                    size: "sm",
                    variant: "grayOutline",
                    disabled,
                    className: cn(
                      "check !px-3 !body-md bg-base-white !text-basic-04 !border-basic-03 min-w-fit",
                      variants === "notice" && "bg-base-white !text-basic-04 !border-basic-03",
                      active && "bg-interactive-active-selected-02 !border-primary-02 !text-interactive-inverse",
                      disabled && "bg-interactive-active-disabled-01 !border-interactive-active-disabled-02 !text-interactive-active-disabled-01"
                    ),
                    onClick: handleClick,
                    "aria-label": active ? "확인 선택됨" : "확인",
                    children: [
                      /* @__PURE__ */ jsx(
                        LineIcon,
                        {
                          icoColor: disabled === true ? "bg-icon-interactive-active-disabled-01" : !active ? "bg-icon-interactive-active-disabled-01" : "bg-icon-interactive-inverse",
                          icoName: "ico-check",
                          type: "xs"
                        }
                      ),
                      btnText
                    ]
                  }
                ) : action !== "none" && /* @__PURE__ */ jsx(
                  "button",
                  {
                    ref: buttonRef,
                    "aria-label": `${title} 약관보기`,
                    className: "pt-0.5",
                    onClick: handleClick,
                    children: /* @__PURE__ */ jsx(LineIcon, { icoColor: "bg-icon-basic-04", icoName: "ico-caret-right" })
                  }
                )
              ]
            }
          ),
          subCheckbox && /* @__PURE__ */ jsx(
            "div",
            {
              className: cn(
                "flex flex-col pl-8 mt-3 gap-2",
                subGrid === true && "grid grid-cols-2 w-full"
              ),
              children: subCheckbox
            }
          )
        ]
      }
    ),
    helpMessage && /* @__PURE__ */ jsx(
      "div",
      {
        className: cn(
          isIndented && "ml-8",
          "mt-2 ml-8",
          action === "confirm" && "ml-0"
        ),
        children: !isError ? hasIcon ? /* @__PURE__ */ jsx(InfoText, { type: isSuccess ? "primary" : "default", text: helpMessage }) : /* @__PURE__ */ jsx("p", { className: "mt-2 body-sm text-center text-state-informative whitespace-pre-line", children: helpMessage }) : hasIcon ? /* @__PURE__ */ jsx(InfoText, { type: "warning", text: helpMessage }) : /* @__PURE__ */ jsx("p", { className: "mt-2 body-sm text-center text-state-danger whitespace-pre-line", children: helpMessage })
      }
    )
  ] });
};
const ConsentRowWrapper = ({
  children,
  className = ""
}) => {
  return /* @__PURE__ */ jsx("div", { className: cn("round-box--section_content", className), children: /* @__PURE__ */ jsx("div", { className: "flex flex-col", children }) });
};
const ConsentTop = ({ children, className }) => {
  return /* @__PURE__ */ jsx("div", { className: cn("round-box--section_header", className), children: /* @__PURE__ */ jsx("strong", { className: "font-weight-bold", children }) });
};

export { ConsentRow, ConsentRowWrapper, ConsentTop, ConsentWrapper };
